import React, { useEffect, useState } from "react";
import { auditTrailData } from "../../lib/axiosUtils";
import MaterialTable from "material-table";
import { ThemeProvider } from '@mui/styles';
import { createTheme } from '@mui/material';
import ShowToast from "components/ShowToast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./audit.css";
import { CALENDAR_ICON, LOADING_SPIN_SMALL } from "assets/constants/Icons";
import { TYPE_FAIL, TYPE_INFO } from "assets/constants/Constants";
import Spinner from 'components/Spinner';

const AuditTrail = () => {
    const mytheme = createTheme({});
    const [auditData, setAuditData] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);

    const fetchAuditTrailData = async (datePayload = {}) => {
        try {
            setIsLoading(true);
            const response = await auditTrailData({}, datePayload);

            if (response?.data?.result) {
                setAuditData(response.data.result);
                setIsLoading(false);
            } else {
                ShowToast("No data found in this date range", TYPE_INFO);
                setIsLoading(false);
            }
        } catch (error) {
            console.error("Error fetching audit trail data:", error);
            ShowToast("Error fetching audit trail data", TYPE_FAIL);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const defaultToDate = new Date();
        const defaultFromDate = new Date();
        defaultFromDate.setDate(defaultFromDate.getDate() - 7);
        setFromDate(defaultFromDate);
        setToDate(defaultToDate);
        const payload = {
            from_date: new Date(defaultFromDate),
            to_date: new Date(defaultToDate)
        };
        fetchAuditTrailData(payload);
    }, []);

    const handleSearch = async () => {
        if (!fromDate && !toDate) {
            ShowToast("Please select both From Date and To Date", TYPE_INFO);
            return;
        }

        if (!fromDate) {
            ShowToast("Please select From Date", TYPE_INFO);
            return;
        }

        if (!toDate) {
            ShowToast("Please select To Date", TYPE_INFO);
            return;
        }

        const from = new Date(fromDate);
        const to = new Date(toDate);

        if (from > to) {
            ShowToast("From Date cannot be greater than To Date", TYPE_FAIL);
            return;
        }

        try {
            setSearchLoading(true);
            const payload = {
                from_date: fromDate,
                to_date: toDate,
            };

            const response = await auditTrailData({}, payload);

            if (response?.data?.result) {
                setAuditData(response.data.result);
                setSearchLoading(false);
            } else {
                ShowToast("No data found in this date range", TYPE_INFO);
                setSearchLoading(false);
            }
        } catch (e) {
            console.error("Search error:", e);
            ShowToast("Error searching data. Please try again!", TYPE_FAIL);
            setSearchLoading(false);
        }
    };


    const columns = [
        {
            title: "Date & Time",
            field: "date",
            cellStyle: { fontWeight: "bolder", color: "#000", fontSize: 14 },
        },
        { title: "Module", field: "moduleName", cellStyle: { color: "#808080", fontSize: 14 } },
        { title: "Activity Type", field: "type", cellStyle: { color: "#808080", fontSize: 14 } },
        {
            title: "Details / Description",
            field: "action",
            cellStyle: { color: "#000", fontSize: 14 },
            render: (rowData) => (
                <div dangerouslySetInnerHTML={{ __html: rowData.action }} />
            ),
        },
        { title: "User", field: "userName", cellStyle: { color: "#808080", fontSize: 14 } },
    ];

    return (
        <>
            <div className="main-content">

                {/* DATE FILTER */}
                <div className="audit-filter-container">
                    {/* FROM DATE */}
                    <label className="ml-2">From:</label>
                    <div className="audit-date-picker">
                        <DatePicker
                            selected={fromDate}
                            onChange={(date) => setFromDate(date)}
                            placeholderText="From Date"
                            dateFormat="dd/MM/yyyy"
                            maxDate={new Date()}           // restrict future dates
                            className="audit-custom-datepicker"
                        />
                        <span className="calendar-icon"><i className={CALENDAR_ICON}></i></span>
                    </div>

                    {/* TO DATE */}
                    <label className="ml-2">To:</label>
                    <div className="audit-date-picker">
                        <DatePicker
                            selected={toDate}
                            onChange={(date) => setToDate(date)}
                            placeholderText="To Date"
                            dateFormat="dd/MM/yyyy"
                            minDate={fromDate || null}
                            maxDate={new Date()}   // restrict TO date to TODAY
                            className="audit-custom-datepicker"
                        />
                        <span className="calendar-icon"><i className={CALENDAR_ICON}></i></span>
                    </div>
                    {
                        searchLoading ? (
                            <div>
                                <i className={LOADING_SPIN_SMALL}></i>
                            </div>
                        ) :
                            <button onClick={handleSearch} className="brand-button ml-5">Search</button>
                    }
                </div>
                <div className="audit-material">
                    {
                        isLoading ? (
                            <Spinner />
                        ) : (
                            <ThemeProvider theme={mytheme}>
                                <MaterialTable
                                    columns={columns}
                                    data={auditData.map((item) => ({
                                        date: item.created_date_time
                                            ? new Date(item.created_date_time).toLocaleString("en-IN", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "2-digit",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                hour12: true,
                                            })
                                            : "",
                                        userName: item.user_name,
                                        moduleName: item.module,
                                        action: item.action,
                                        type: item.type,
                                    }))}
                                    options={{
                                        headerStyle: {
                                            color: "#999998",
                                            fontWeight: "bolder",
                                            fontSize: 15,
                                        },
                                        sorting: true,
                                        exportButton: true,
                                        filtering: true,
                                        pageSize: 5,
                                        pageSizeOptions: [5, 10, 15, 20],
                                    }}
                                />
                            </ThemeProvider>
                        )
                    }
                </div>
            </div>
        </>
    );
};

export default AuditTrail;